import { LoyaltyTagAPIPromiseClient } from "gen/witbooking/loyalty/tag/v1/loyalty_tag_api_grpc_web_pb";
import {
  CreateMultipleTagsExcelRequest,
  CreateTagRequest,
  DeleteTagRequest,
  DownloadTagsTemplateExcelRequest,
  GetTagRequest,
  ListTagsRequest,
  UpdateTagRequest,
} from "gen/witbooking/loyalty/tag/v1/loyalty_tag_api_pb";
import { LoyaltyTag } from "../../domain/LoyaltyTag";
import {
  LoyaltyTagMultiUpdateResult,
  LoyaltyTagMultiUpdateResultError,
  LoyaltyTagMultiUpdateResultErrorCode,
} from "../../domain/LoyaltyTagMultiUpdateResult";
import { LoyaltyTagMapper } from "../mappers/LoyaltyTagMapper";

export class LoyaltyTagGateway {
  constructor(private readonly grpcClient: LoyaltyTagAPIPromiseClient) {}

  async getAll(brandId: string, includeGlobals: boolean = false): Promise<LoyaltyTag[]> {
    console.debug(`getting all tags for brand [${brandId}], globals: [${includeGlobals}]`);
    const request = new ListTagsRequest();
    request.setBrandid(brandId);
    request.setGlobals(includeGlobals);

    return (await this.grpcClient.listTags(request)).getTagsList().map(LoyaltyTagMapper.fromGrpc);
  }

  async get(brandId: string, id: string): Promise<LoyaltyTag> {
    console.debug(`getting tag with id [${id}] for brand [${brandId}]`);
    const request = new GetTagRequest();
    request.setBrandid(brandId);
    request.setId(id);

    const tag = (await this.grpcClient.getTag(request)).getTag();
    if (tag == null) throw new Error(`Tag [${id}] not found`);

    return LoyaltyTagMapper.fromGrpc(tag);
  }

  async save(brandId: string, tag: LoyaltyTag): Promise<void> {
    console.debug(`saving tag [${tag.id}]`);
    const request = new CreateTagRequest();
    request.setBrandid(brandId);
    request.setTag(LoyaltyTagMapper.toGrpc(tag, brandId));

    await this.grpcClient.createTag(request);
  }

  async saveAll(brandId: string, file: File): Promise<LoyaltyTagMultiUpdateResult> {
    console.debug(`saving all tags for brand [${brandId}]`);
    const request = new CreateMultipleTagsExcelRequest();
    request.setBrandid(brandId);
    request.setContent(new Uint8Array(await file.arrayBuffer()));

    try {
      const response = await this.grpcClient.createMultipleTagsExcel(request);
      return LoyaltyTagMultiUpdateResult.of({
        created: response.getCreated(),
        deleted: response.getDeleted(),
        errors: response.getErrorsList().map(error =>
          LoyaltyTagMultiUpdateResultError.of({
            ticker: error.getTicker(),
            code: error.getErrorcode() as LoyaltyTagMultiUpdateResultErrorCode,
            message: error.getErrormessage(),
          })
        ),
      });
    } catch (e) {
      console.error("Error saving all tags", e);
      return LoyaltyTagMultiUpdateResult.of({
        created: 0,
        deleted: 0,
        errors: [
          LoyaltyTagMultiUpdateResultError.of({
            ticker: "",
            code: LoyaltyTagMultiUpdateResultErrorCode.UNKNOWN_ERROR,
            message:
              "An error occurred while importing the file: " +
              (e instanceof Error ? e.message : "unknown error"),
          }),
        ],
      });
    }
  }

  async downloadSaveAllTemplate(brandId: string): Promise<Uint8Array> {
    const request = new DownloadTagsTemplateExcelRequest();
    request.setBrandid(brandId);

    const response = await this.grpcClient.downloadTagsTemplateExcel(request);
    return response.getContent() as Uint8Array;
  }

  async update(brandId: string, tag: LoyaltyTag): Promise<void> {
    console.debug(`updating tag [${tag.id}]`);
    const request = new UpdateTagRequest();
    request.setBrandid(brandId);
    request.setTag(LoyaltyTagMapper.toGrpc(tag, brandId));

    await this.grpcClient.updateTag(request);
  }

  async delete(brandId: string, id: string): Promise<void> {
    console.debug(`deleting tag with id [${id}]`);
    const request = new DeleteTagRequest();
    request.setBrandid(brandId);
    request.setId(id);

    await this.grpcClient.deleteTag(request);
  }
}
